import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, finalize, map, shareReplay, tap } from 'rxjs/operators';
import { IExecuteRuleRequest, IExecuteRule } from 'common/model';
import { AppHttpClient } from 'common/service/app-http-client.service';
import { prepareClientFormApiUrl } from 'projects/evide-client/src/app/app.const';
import { environment } from 'src/environments/environment';

const HttpServiceMethods = {
  ExecuteRule: 'EvideInputContentForm/ExecuteRule',
  ExecuteRuleBatch: 'EvideInputContentForm/ExecuteRulesBatch',
  getExecuteRuleUrl: () => prepareClientFormApiUrl('ExecuteRule'),
  getExecuteRuleBatchUrl: () => prepareClientFormApiUrl('ExecuteRulesBatch')
};

type Cached<T> = { value: T; expires: number };

@Injectable({ providedIn: 'root' })
export class ExecuteRuleService {
  private cache = new Map<string, Cached<IExecuteRule>>();
  private maxCacheSize = 1000;
  private defaultTtlMs = 1000;

  private inflight = new Map<string, Observable<IExecuteRule>>();

  constructor(private httpService: AppHttpClient) {}

  public executeRule(
    body: IExecuteRuleRequest,
    opts?: { bypassCache?: boolean; ttlMs?: number }
  ): Observable<IExecuteRule> {
    const key = this.getCacheKey(body);
    const inflightKey = opts?.bypassCache ? key + '|nocache' : key;

    this.pruneExpired();

    if (!opts?.bypassCache) {
      const cached = this.getFreshFromCache(key);
      if (cached) return of(cached);
    }

    const existing = this.inflight.get(inflightKey);
    if (existing) return existing;

    const method = (window as any).clientApp
      ? HttpServiceMethods.getExecuteRuleUrl()
      : `${environment.baseUrl}${HttpServiceMethods.ExecuteRule}`;

    const req$ = this.httpService.post<IExecuteRule>(method, body).pipe(
      tap((resp) => {
        if (!opts?.bypassCache) {
          this.cacheResponseIfUseful(
            key,
            resp,
            opts?.ttlMs ?? this.defaultTtlMs
          );
        }
      }),
      catchError((err) => {
        const stale = this.getStaleFromCache(key);
        if (stale) return of(stale);
        return throwError(() => err);
      }),
      shareReplay(1),
      finalize(() => this.inflight.delete(inflightKey))
    );

    this.inflight.set(inflightKey, req$);
    return req$;
  }

  public executeRulesBatch(
    body: IExecuteRuleRequest[],
    opts?: { bypassCache?: boolean; ttlMs?: number }
  ): Observable<IExecuteRule[]> {
    const ttl = opts?.ttlMs ?? this.defaultTtlMs;
    this.pruneExpired();

    const results: Array<IExecuteRule | undefined> = new Array(body.length);
    const uncachedRequests: IExecuteRuleRequest[] = [];
    const uncachedIndexes: number[] = [];
    const uncachedKeys: string[] = [];

    for (let i = 0; i < body.length; i++) {
      const req = body[i];
      const key = this.getCacheKey(req);
      if (!opts?.bypassCache) {
        const cached = this.getFreshFromCache(key);
        if (cached) {
          results[i] = cached;
          continue;
        }
      }
      uncachedRequests.push(req);
      uncachedIndexes.push(i);
      uncachedKeys.push(this.getCacheKey(req));
    }

    if (uncachedRequests.length === 0) {
      return of(results as IExecuteRule[]);
    }

    const method = (window as any).clientApp
      ? HttpServiceMethods.getExecuteRuleBatchUrl()
      : `${environment.baseUrl}${HttpServiceMethods.ExecuteRuleBatch}`;

    return this.httpService.post<IExecuteRule[]>(method, uncachedRequests).pipe(
      map((responses) => {
        for (let i = 0; i < responses.length; i++) {
          const idx = uncachedIndexes[i];
          const key = uncachedKeys[i];
          const resp = responses[i];
          results[idx] = resp;
          if (!opts?.bypassCache) {
            this.cacheResponseIfUseful(key, resp, ttl);
          }
        }
        return results as IExecuteRule[];
      }),
      catchError((err) => {
        let filledFromStale = true;
        for (let i = 0; i < uncachedIndexes.length; i++) {
          const idx = uncachedIndexes[i];
          const key = uncachedKeys[i];
          const stale = this.getStaleFromCache(key);
          if (stale) {
            results[idx] = stale;
          } else {
            filledFromStale = false;
          }
        }
        if (filledFromStale) {
          return of(results as IExecuteRule[]);
        }
        return throwError(() => err);
      })
    );
  }

  public invalidateForInput(evideInputContentGuid: string): void {
    const prefix = `${evideInputContentGuid}:`;
    for (const k of Array.from(this.cache.keys())) {
      if (k.startsWith(prefix)) this.cache.delete(k);
    }
    for (const k of Array.from(this.inflight.keys())) {
      if (k.startsWith(prefix)) this.inflight.delete(k);
    }
  }

  private getFreshFromCache(key: string): IExecuteRule | undefined {
    const entry = this.cache.get(key);
    if (!entry) return undefined;
    if (Date.now() > entry.expires) return undefined;
    this.cache.delete(key);
    this.cache.set(key, entry);
    return entry.value;
  }

  private getStaleFromCache(key: string): IExecuteRule | undefined {
    const entry = this.cache.get(key);
    return entry?.value;
  }

  private cacheResponseIfUseful(
    key: string,
    response: IExecuteRule,
    ttlMs: number
  ) {
    if (!response) return;

    const isEmpty =
      (response.singleValue === null ||
        response.singleValue === undefined ||
        response.singleValue === '') &&
      (!response.multiValues || Object.keys(response.multiValues).length === 0);

    if (isEmpty) return;

    const expires = Date.now() + Math.max(100, ttlMs | 0); // minimum 100ms
    this.cache.set(key, { value: response, expires });
    this.evictLRUIfNeeded();
  }

  private evictLRUIfNeeded() {
    if (this.cache.size <= this.maxCacheSize) return;
    const excess = this.cache.size - this.maxCacheSize;
    let n = 0;
    for (const k of this.cache.keys()) {
      this.cache.delete(k);
      if (++n >= excess) break;
    }
  }

  private pruneExpired() {
    const now = Date.now();
    for (const [k, v] of this.cache.entries()) {
      if (now > v.expires) this.cache.delete(k);
    }
  }

  private getCacheKey(request: IExecuteRuleRequest): string {
    return `${request.evideInputContentGuid}:${this.stableStringify(
      request.parameters || {}
    )}`;
  }

  private stableStringify(obj: any): string {
    if (obj === null || obj === undefined) return '';
    if (typeof obj !== 'object') return String(obj);

    if (Array.isArray(obj)) {
      return `[${obj.map((v) => this.stableStringify(v)).join(',')}]`;
    }

    const keys = Object.keys(obj).sort();
    const entries = keys.map(
      (k) => `"${k}":${this.stableStringify((obj as any)[k])}`
    );
    return `{${entries.join(',')}}`;
  }
}
